import { style } from '@vanilla-extract/css';
import { vars } from '@/shared/ui/theme/contract.css';

/**
 * 발송 이력 (퀘스트 7ca8908c) — 섭외 IA §3-6.
 *
 * 회차 여섯 칸을 한 줄로 읽는 표다. 카드로 쌓으면 회차가 늘수록 세로로만 길어져서 **어느 나라
 * 몇 회차였는지를 훑어 비교할 수가 없다** — 이력이 답하는 질문이 그 비교다.
 */
export const panel = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.sm,
});

export const head = style({
  display: 'flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  gap: vars.spacing.gap.md,
});

export const heading = style({
  margin: 0,
  // 형제 셋이 같은 크기다 — 제목이 경계 노릇을 하므로 본문과 또렷이 갈려야 한다(2026-09-28).
  fontSize: vars.typography.fontSize.lg,
  fontWeight: String(vars.typography.fontWeight.bold),
  color: vars.typography.textColor.primary,
});

/**
 * 상자를 두르지 않는다(2026-09-28). 메일 면의 형제(후보 선택 · 회신 큐 · 발송 이력)를
 * 구분선으로 가르기로 하면서, 여기에 테두리와 배경을 두면 선 옆에 상자가 겹쳐 선다.
 */
export const frame = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.spacing.gap.lg,
});

/** 화면에는 안 보이고 이름만 준다. */
export const srOnly = style({
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
});

export const empty = style({
  margin: 0,
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.tertiary,
});

/**
 * 표 테두리와 둥근 모서리 (2026-09-28).
 *
 * `border-collapse: collapse` 는 칸 테두리를 합치면서 `border-radius` 를 무시한다. `separate`
 * + `border-spacing: 0` 으로 바꾸면 모서리가 살고 칸 사이는 그대로 붙는다. 대신 칸마다 아래
 * 선을 그으면 마지막 줄에서 표 테두리와 겹쳐 두 겹이 되므로, 마지막 줄만 그 선을 지운다.
 *
 * 테두리 색은 행 사이 선과 같다 — 표 하나가 같은 굵기의 격자로 읽혀야 한다.
 */
export const table = style({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
  border: `1px solid ${vars.surface.border.subtle}`,
  borderRadius: vars.spacing.borderRadius.lg,
  overflow: 'hidden',
});

/**
 * 머리줄 — **배경과 아래 선으로 몸통과 가른다**(2026-09-28).
 *
 * 열 이름과 값이 같은 바탕에 있으면 첫 줄이 데이터인지 이름인지 훑어서는 안 갈린다. 배경은
 * 옅게(0.16) 깔고 아래 선은 행 사이 선보다 한 겹 진하게 둬서 「여기부터 몸통」 이 읽히게 한다.
 */
export const th = style({
  // 위아래 `md` — 열 이름이 배경 안에서 숨을 쉬어야 머리줄로 읽힌다(2026-09-28).
  padding: `${vars.spacing.padding.md} ${vars.spacing.padding.sm}`,
  textAlign: 'left',
  // `2xs`(11px) 는 값보다 너무 작아 이름이 눌렸다. 한 눈금 위인 `xs` 로 올린다.
  fontSize: vars.typography.fontSize.xs,
  fontWeight: String(vars.typography.fontWeight.medium),
  color: vars.typography.textColor.label,
  background: vars.surface.highlight.medium,
  borderBottom: `1px solid ${vars.opacityScale.whiteOverlay.light}`,
  whiteSpace: 'nowrap',
});

export const row = style({
  selectors: { '&:hover': { background: vars.surface.card.hover } },
});

export const td = style({
  padding: `${vars.spacing.padding.md} ${vars.spacing.padding.sm}`,
  borderBottom: `1px solid ${vars.surface.border.subtle}`,
  verticalAlign: 'middle',
  selectors: {
    // 마지막 줄의 아래 선은 표 테두리와 같은 자리라 두 겹으로 굵어진다.
    'tr:last-child &': { borderBottom: 'none' },
  },
});

/**
 * 대상 열 — **좌측 정렬**이다.
 *
 * 우측 정렬은 여러 수를 위아래로 견줄 때 값이 큰데, 이 열은 옆의 상태 배지와 한 줄로 읽히는
 * 자리라 오른쪽으로 밀면 제목과 상태 사이가 비어 줄이 끊긴다. 자릿수만 `tabular-nums` 로 맞춘다.
 */
export const numeric = style({
  textAlign: 'left',
  fontVariantNumeric: 'tabular-nums',
});

export const roundCell = style({
  display: 'inline-flex',
  alignItems: 'center',
  gap: vars.spacing.gap.sm,
});

/** 나라는 배지, 번호는 글자 — 훑을 때 나라가 먼저 잡힌다. */
export const countryTag = style({
  padding: `${vars.spacing.padding['2xs']} ${vars.spacing.padding.xs}`,
  borderRadius: vars.spacing.borderRadius.sm,
  background: vars.bright.badge.primary.background,
  border: `1px solid ${vars.bright.badge.primary.border}`,
  fontSize: vars.typography.fontSize['3xs'],
  color: vars.typography.textColor.secondary,
});

export const sequence = style({
  fontSize: vars.typography.fontSize.md,
  fontWeight: String(vars.typography.fontWeight.bold),
  color: vars.typography.textColor.primary,
});

export const subject = style({
  display: 'block',
  fontSize: vars.typography.fontSize.sm,
  color: vars.typography.textColor.secondary,
});

export const subLine = style({
  display: 'block',
  marginTop: vars.spacing.gap.xs,
  fontSize: vars.typography.fontSize['2xs'],
  color: vars.typography.textColor.softMuted,
});

export const plain = style({
  fontSize: vars.typography.fontSize.xs,
  color: vars.typography.textColor.tertiary,
  whiteSpace: 'nowrap',
});

export const target = style({
  fontSize: vars.typography.fontSize.sm,
  color: vars.typography.textColor.primary,
});

const pillBase = style({
  display: 'inline-block',
  padding: `${vars.spacing.padding['2xs']} ${vars.spacing.padding.sm}`,
  borderRadius: vars.spacing.borderRadius.full,
  fontSize: vars.typography.fontSize['3xs'],
  whiteSpace: 'nowrap',
});

export const sentPill = style([
  pillBase,
  {
    background: vars.bright.status.successFaint,
    border: `1px solid ${vars.bright.status.successGlow}`,
    color: vars.bright.status.success,
  },
]);

/** **실패가 아니라 확인 대기**다 — 빨강으로 두면 재발송을 부른다. */
export const waitPill = style([
  pillBase,
  {
    background: vars.bright.status.warningBg,
    border: `1px solid ${vars.bright.status.warningBorder}`,
    color: vars.bright.status.warning,
  },
]);

/** 예약 대기·처리 중·취소 — 문제가 아니라 진행 상태라 중립 표면이다(96445435). */
export const neutralPill = style([
  pillBase,
  {
    background: vars.surface.card.hover,
    border: `1px solid ${vars.surface.border.subtle}`,
    color: vars.typography.textColor.secondary,
  },
]);

/** 상태 칸 안에서 배지 아래 줄로 서는 예약 취소 버튼. */
export const statusStack = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: vars.spacing.gap.xs,
});

/** 테스트 발송은 지표에서 빠진다 — 이력에서도 구분이 서야 한다. */
export const testPill = style([
  pillBase,
  {
    marginRight: vars.spacing.gap.xs,
    background: vars.surface.card.hover,
    border: `1px solid ${vars.surface.border.subtle}`,
    color: vars.typography.textColor.softMuted,
  },
]);

export const footNote = style({
  margin: 0,
  fontSize: vars.typography.fontSize['2xs'],
  lineHeight: String(vars.typography.lineHeight.relaxed),
  color: vars.typography.textColor.softMuted,
});

export const strong = style({
  color: vars.typography.textColor.secondary,
});
